import { useState, useEffect } from 'react';
import {
    Drawer,
    IconButton,
    CircularProgress,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getEntryDetails } from '../api/api';
import { useTheme } from '@mui/material/styles';

const EntryInfoDrawer = ({ open, onClose, parentEntryIds, workspaceId }) => {
    const [parentEntries, setParentEntries] = useState([]);
    const [fetched, setFetched] = useState(false);
    const [loading, setLoading] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        if (open && !fetched && parentEntryIds?.length > 0) {
            const fetchParentEntries = async () => {
                setLoading(true);
                try {
                    const response = await getEntryDetails(workspaceId, parentEntryIds);
                    if (response?.data?.entry) {
                        setParentEntries(response.data.entry);
                    } else {
                        setParentEntries([]);
                    }
                } catch (error) {
                    console.error('Error fetching parent entries:', error);
                    setParentEntries([]);
                } finally {
                    setLoading(false);
                    setFetched(true);
                }
            };
            fetchParentEntries();
        }
    }, [open, fetched, workspaceId, parentEntryIds]);

    const handleOpenEntry = (id) => {
        const baseUrl = process.env.REACT_APP_FRONTEND_URL;
        const url = `${baseUrl}/${workspaceId}/edit-entry/${id}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: 400,
                    bgcolor: '#FAFAFA'
                }
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{
                    p: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'background.paper'
                }}>
                    <Typography variant="h6">Entry Information</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                        Parent Entries
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : parentEntries.length > 0 ? (
                        <List disablePadding>
                            {parentEntries.map((entry, index) => (
                                <ListItem
                                    key={entry._id || index}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        mb: 1.5,
                                        cursor: 'pointer',
                                        bgcolor: 'background.paper',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            bgcolor: 'action.hover',
                                            borderColor: 'primary.main',
                                            transform: 'translateY(-1px)',
                                            boxShadow: 1
                                        }
                                    }}
                                    onClick={() => handleOpenEntry(entry._id)}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <ArrowRightIcon sx={{ color: 'primary.main', mr: 1 }} />
                                        <ListItemText
                                            primary={
                                                <Typography variant="subtitle2" color="primary.main">
                                                    {entry.identifier || 'No Identifier'}
                                                </Typography>
                                            }
                                            secondary={
                                                <Typography variant="caption" color="text.secondary">
                                                    ID: {entry._id}
                                                </Typography>
                                            }
                                        />
                                        <OpenInNewIcon sx={{ fontSize: 16, color: 'text.disabled', ml: 1 }} />
                                    </Box>
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Box sx={{
                            p: 3,
                            border: '1px dashed',
                            borderColor: 'divider',
                            borderRadius: 1,
                            textAlign: 'center',
                            bgcolor: 'background.paper'
                        }}>
                            <Typography variant="body2" color="text.secondary">
                                No parent entries found.
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Drawer>
    );
};

export default EntryInfoDrawer;
